import {
  Terrain, Animal, EdgeType, Direction,
  type Edges, type TileDefinition, type LevelConfig,
} from "./types"

// ── Game Constants ──

export const GRID_SIZE = 12
export const DECK_SIZE = 60
export const MARKET_SIZE = 3

export const WOLF_RANGE = 3
export const BEAR_RANGE = 2
export const FOREST_PROTECTION_MIN_DISTANCE = 2
export const MOUNTAIN_CONSECUTIVE_BLOCK = 2

export const COMBO_MAX = 5
export const COMBO_INCREMENT = 1
export const COMBO_RESET = 1

export const SCORE_PLACEMENT = 10
export const SCORE_SHEEP_ALIVE = 100
export const SCORE_DEER_ALIVE = 120
export const SCORE_WOLF_ALIVE = 80
export const SCORE_BEAR_ALIVE = 150
export const SCORE_PERFECT_ISLAND = 200
export const PENALTY_HERBIVORE_DEATH = -50

export const PERFECT_ISLAND_MIN_TILES = 6

export const VICTORY_THRESHOLD = 0.60
export const MINOR_VICTORY_THRESHOLD = 0.40

// ── Predation Graph ──

export const PREY_MAP: Record<Animal, Animal[]> = {
  [Animal.Wolf]: [Animal.Sheep, Animal.Deer],
  [Animal.Bear]: [Animal.Sheep, Animal.Deer, Animal.Wolf],
  [Animal.Sheep]: [],
  [Animal.Deer]: [],
}

export const PREDATOR_RANGE: Partial<Record<Animal, number>> = {
  [Animal.Wolf]: WOLF_RANGE,
  [Animal.Bear]: BEAR_RANGE,
}

// ── Edge Patterns ──

export const EDGE_PATTERNS: Record<string, Edges> = {
  all_land: {
    [Direction.N]: EdgeType.Land,
    [Direction.E]: EdgeType.Land,
    [Direction.S]: EdgeType.Land,
    [Direction.W]: EdgeType.Land,
  },
  all_water: {
    [Direction.N]: EdgeType.Water,
    [Direction.E]: EdgeType.Water,
    [Direction.S]: EdgeType.Water,
    [Direction.W]: EdgeType.Water,
  },
  cliff_north: {
    [Direction.N]: EdgeType.Cliff,
    [Direction.E]: EdgeType.Land,
    [Direction.S]: EdgeType.Land,
    [Direction.W]: EdgeType.Land,
  },
  river_vertical: {
    [Direction.N]: EdgeType.Water,
    [Direction.E]: EdgeType.Land,
    [Direction.S]: EdgeType.Water,
    [Direction.W]: EdgeType.Land,
  },
  river_horizontal: {
    [Direction.N]: EdgeType.Land,
    [Direction.E]: EdgeType.Water,
    [Direction.S]: EdgeType.Land,
    [Direction.W]: EdgeType.Water,
  },
}

// ── Tile Catalog (60 tiles) ──

export const TILE_CATALOG: TileDefinition[] = [
  // Prairies (26)
  { terrain: Terrain.Prairie, animal: null, edgePattern: "all_land", count: 12 },
  { terrain: Terrain.Prairie, animal: Animal.Sheep, edgePattern: "all_land", count: 8 },
  { terrain: Terrain.Prairie, animal: Animal.Deer, edgePattern: "all_land", count: 2 },
  { terrain: Terrain.Prairie, animal: Animal.Wolf, edgePattern: "all_land", count: 3 },
  { terrain: Terrain.Prairie, animal: Animal.Bear, edgePattern: "all_land", count: 1 },
  // Forests (12)
  { terrain: Terrain.Forest, animal: null, edgePattern: "all_land", count: 4 },
  { terrain: Terrain.Forest, animal: Animal.Sheep, edgePattern: "all_land", count: 4 },
  { terrain: Terrain.Forest, animal: Animal.Deer, edgePattern: "all_land", count: 2 },
  { terrain: Terrain.Forest, animal: Animal.Wolf, edgePattern: "all_land", count: 2 },
  // Mountains (8)
  { terrain: Terrain.Mountain, animal: null, edgePattern: "all_land", count: 4 },
  { terrain: Terrain.Mountain, animal: Animal.Wolf, edgePattern: "all_land", count: 2 },
  { terrain: Terrain.Mountain, animal: Animal.Bear, edgePattern: "all_land", count: 2 },
  // Lakes (4)
  { terrain: Terrain.Lake, animal: null, edgePattern: "all_water", count: 4 },
  // Cliffs (6)
  { terrain: Terrain.Prairie, animal: null, edgePattern: "cliff_north", count: 3 },
  { terrain: Terrain.Prairie, animal: Animal.Sheep, edgePattern: "cliff_north", count: 2 },
  { terrain: Terrain.Prairie, animal: Animal.Wolf, edgePattern: "cliff_north", count: 1 },
  // Rivers (4)
  { terrain: Terrain.Prairie, animal: null, edgePattern: "river_vertical", count: 3 },
  { terrain: Terrain.Prairie, animal: Animal.Sheep, edgePattern: "river_vertical", count: 1 },
]

// ── Level Configurations ──

export function getLevelConfig(level: number): LevelConfig {
  switch (level) {
    case 0:
      return { level: 0, gridSize: 4, deckTiles: 3, marketSize: 1, description: "Premiere ile" }
    case 1:
      return { level: 1, gridSize: 6, deckTiles: 8, marketSize: 2, description: "Le loup arrive" }
    case 2:
      return { level: 2, gridSize: 6, deckTiles: 12, marketSize: 3, description: "Barrieres naturelles" }
    case 3:
      return { level: 3, gridSize: 8, deckTiles: 15, marketSize: 3, description: "La foret protege" }
    default:
      return { level, gridSize: GRID_SIZE, deckTiles: DECK_SIZE, marketSize: MARKET_SIZE, description: "Jeu libre" }
  }
}
